import { OFFER_PLN149_100_V1, PREPAID_CATALOGUE } from '@acs/shared';
import type { IconName } from '@acs/ui';
import type { TranslationKey } from '../../i18n/index.js';

/**
 * Public copy lives in all five locale catalogues; this file defines its structure.
 * See docs/landing-copy-audit.md for the implementation behind the claims.
 *
 * Stripe quotes billing amounts during activation. Do not publish test-fixture
 * prices or infer a business's currency from its UI locale. The included-minute
 * offer below applies to PLN subscriptions only.
 */
export const INCLUDED_MINUTES = OFFER_PLN149_100_V1.includedSecondsPerPeriod / 60;
export const POLISH_PACKAGES = PREPAID_CATALOGUE.packages
  .filter((pkg) => pkg.enabled && pkg.currency === 'PLN')
  .map((pkg) => pkg.seconds / 60);

export interface NavLink {
  id: string;
  labelKey: TranslationKey;
}

export const NAV_LINKS: NavLink[] = [
  { id: 'how', labelKey: 'landing.nav.how' },
  { id: 'features', labelKey: 'landing.nav.features' },
  { id: 'pricing', labelKey: 'landing.nav.pricing' },
  { id: 'faq', labelKey: 'landing.nav.faq' },
];

/** Appointment-based trades, not endorsements or a claim to cover clinical workflows. */
export const INDUSTRY_KEYS: TranslationKey[] = [
  'landing.industries.i1',
  'landing.industries.i2',
  'landing.industries.i3',
  'landing.industries.i4',
  'landing.industries.i5',
  'landing.industries.i6',
];

export interface IconCard {
  icon: IconName;
  titleKey: TranslationKey;
  bodyKey: TranslationKey;
}

/** Configure and try, verify forwarding, then activate the subscription. */
export const HOW_STEPS: IconCard[] = [
  { icon: 'list', titleKey: 'landing.how.s1.title', bodyKey: 'landing.how.s1.body' },
  { icon: 'phone-incoming', titleKey: 'landing.how.s2.title', bodyKey: 'landing.how.s2.body' },
  { icon: 'calendar', titleKey: 'landing.how.s3.title', bodyKey: 'landing.how.s3.body' },
];

/** Calls, day-to-day scheduling, then the details owners need around each booking. */
export const FEATURES: IconCard[] = [
  { icon: 'clock', titleKey: 'landing.features.f1.title', bodyKey: 'landing.features.f1.body' },
  { icon: 'refresh', titleKey: 'landing.features.f2.title', bodyKey: 'landing.features.f2.body' },
  { icon: 'globe', titleKey: 'landing.features.f3.title', bodyKey: 'landing.features.f3.body' },
  { icon: 'calendar', titleKey: 'landing.features.f4.title', bodyKey: 'landing.features.f4.body' },
  { icon: 'users', titleKey: 'landing.features.f5.title', bodyKey: 'landing.features.f5.body' },
  { icon: 'settings', titleKey: 'landing.features.f6.title', bodyKey: 'landing.features.f6.body' },
  { icon: 'list', titleKey: 'landing.features.f7.title', bodyKey: 'landing.features.f7.body' },
  { icon: 'phone', titleKey: 'landing.features.f8.title', bodyKey: 'landing.features.f8.body' },
  { icon: 'check', titleKey: 'landing.features.f9.title', bodyKey: 'landing.features.f9.body' },
];

export const OBJECTIONS: IconCard[] = [
  { icon: 'shield', titleKey: 'landing.trust.o1.title', bodyKey: 'landing.trust.o1.body' },
  { icon: 'phone', titleKey: 'landing.trust.o2.title', bodyKey: 'landing.trust.o2.body' },
  { icon: 'check', titleKey: 'landing.trust.o3.title', bodyKey: 'landing.trust.o3.body' },
  { icon: 'mic', titleKey: 'landing.trust.o4.title', bodyKey: 'landing.trust.o4.body' },
];

export interface FaqEntry {
  questionKey: TranslationKey;
  answerKey: TranslationKey;
}

export const FAQ: FaqEntry[] = [
  { questionKey: 'landing.faq.q1', answerKey: 'landing.faq.a1' },
  { questionKey: 'landing.faq.q2', answerKey: 'landing.faq.a2' },
  { questionKey: 'landing.faq.q3', answerKey: 'landing.faq.a3' },
  { questionKey: 'landing.faq.q4', answerKey: 'landing.faq.a4' },
  { questionKey: 'landing.faq.q5', answerKey: 'landing.faq.a5' },
  { questionKey: 'landing.faq.q6', answerKey: 'landing.faq.a6' },
  { questionKey: 'landing.faq.q7', answerKey: 'landing.faq.a7' },
  { questionKey: 'landing.faq.q8', answerKey: 'landing.faq.a8' },
  { questionKey: 'landing.faq.q9', answerKey: 'landing.faq.a9' },
  { questionKey: 'landing.faq.q10', answerKey: 'landing.faq.a10' },
];

export interface CallTurn {
  speaker: 'assistant' | 'caller';
  key: TranslationKey;
}

/** An illustration: the caller confirms the full read-back before a booking is made. */
export const SAMPLE_CALL: CallTurn[] = [
  { speaker: 'assistant', key: 'landing.call.t1' },
  { speaker: 'caller', key: 'landing.call.t2' },
  { speaker: 'assistant', key: 'landing.call.t3' },
  { speaker: 'caller', key: 'landing.call.t4' },
  { speaker: 'assistant', key: 'landing.call.t5' },
  { speaker: 'caller', key: 'landing.call.t6' },
  { speaker: 'assistant', key: 'landing.call.t7' },
  { speaker: 'caller', key: 'landing.call.t8' },
  { speaker: 'assistant', key: 'landing.call.t9' },
];

export interface BillingItem {
  nameKey: TranslationKey;
  valueKey: TranslationKey;
  detailKey: TranslationKey;
  descKey: TranslationKey;
  featured?: boolean;
}

/** Parts of the current billing model, not three competing subscription tiers. */
export const BILLING_ITEMS: BillingItem[] = [
  {
    nameKey: 'landing.pricing.subscription.name',
    valueKey: 'landing.pricing.subscription.value',
    detailKey: 'landing.pricing.subscription.detail',
    descKey: 'landing.pricing.subscription.desc',
    featured: true,
  },
  {
    nameKey: 'landing.pricing.topup.name',
    valueKey: 'landing.pricing.topup.value',
    detailKey: 'landing.pricing.topup.detail',
    descKey: 'landing.pricing.topup.desc',
  },
  {
    nameKey: 'landing.pricing.control.name',
    valueKey: 'landing.pricing.control.value',
    detailKey: 'landing.pricing.control.detail',
    descKey: 'landing.pricing.control.desc',
  },
];

/** Market names stay in their own languages. */
export const MARKETS = [
  { flag: '🇵🇱', name: 'Polska' },
  { flag: '🇩🇪', name: 'Deutschland' },
  { flag: '🇫🇷', name: 'France' },
];
